import React from 'react';
import { PreviewProvider } from '../../contexts/PreviewContext';
import { ThemeProvider } from '../../contexts/ThemeContext';
import { CustomColorsProvider } from '../../contexts/CustomColorsContext';
import { previewData } from './previewData';

interface ComponentInfo {
  id: string;
  name: string;
  component: any;
}

interface ThemeManagerPreviewProps {
  selectedTheme: string;
  selectedComponent: string;
  customColors: Record<string, Record<string, string>>;
  components: ComponentInfo[];
}

export const ThemeManagerPreview: React.FC<ThemeManagerPreviewProps> = ({
  selectedTheme,
  selectedComponent,
  customColors,
  components,
}) => {
  const renderComponentPreview = () => {
    const component = components.find(c => c.id === selectedComponent);
    if (!component) return null;

    const Component = component.component as any;
    const data = previewData[selectedComponent as keyof typeof previewData];

    // Map component IDs to their correct types (handle kebab-case)
    const componentType =
      selectedComponent === 'textBlocks'
        ? 'text-blocks'
        : selectedComponent === 'mediaText'
          ? 'media-text'
          : selectedComponent;

    // Wrap data in component structure as expected by components
    const componentProp = {
      id: `preview-${selectedComponent}`,
      type: componentType,
      data: data,
      order: 0,
      visible: true,
    };

    return (
      <PreviewProvider isPreviewMode={true} siteId="theme-preview">
        <ThemeProvider theme={selectedTheme}>
          <CustomColorsProvider customColors={customColors}>
            <div className="w-full bg-white">
              <Component
                component={componentProp}
                key={`${selectedComponent}-${JSON.stringify(customColors)}`}
              />
            </div>
          </CustomColorsProvider>
        </ThemeProvider>
      </PreviewProvider>
    );
  };

  return (
    <div className="mb-8">
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="px-6 py-4 bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">
              {components.find(c => c.id === selectedComponent)?.name} Preview
            </h3>
            <span className="text-sm text-gray-600">
              Theme: <span className="font-medium text-primary-600">{selectedTheme}</span>
            </span>
          </div>
        </div>
        <div className="bg-gray-50 p-8">
          <div
            className="bg-white rounded-lg shadow-sm overflow-auto"
            style={{ maxHeight: '600px' }}
          >
            {renderComponentPreview()}
          </div>
        </div>
      </div>
    </div>
  );
};
